import { CrossOrgCostResponse } from '@/services/admin/get-cross-org-cost';

// Quote per RFC-4180 and neutralize spreadsheet formula injection: a text cell
// starting with =, +, -, @, tab, or CR is treated as a formula by Excel/Sheets,
// so prefix it with a single quote. Numbers are emitted as-is.
const escapeCsv = (value: string | number): string => {
  if (typeof value === 'number') return String(value);
  const s = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const HEADER = [
  'date_range',
  'org',
  'provider',
  'model',
  'base_cost_usd',
  'markup_pct',
  'marked_up_cost_usd',
  'margin_usd',
];

/**
 * Build the cost-report CSV (one row per org → provider → model). Pure and
 * unit-tested; the download side effects live in `exportCostCsv`.
 */
export function buildCostCsv(report: CrossOrgCostResponse): string {
  const range = `${report.start_date} to ${report.end_date}`;
  const rows: (string | number)[][] = [];
  for (const org of report.orgs) {
    if (!org.cost_available) continue;
    for (const provider of org.providers) {
      for (const model of provider.models) {
        rows.push([
          range,
          org.org_name ?? org.org_id,
          provider.provider,
          model.model,
          model.base_cost_usd,
          org.markup_percentage,
          model.marked_up_cost_usd,
          model.marked_up_cost_usd - model.base_cost_usd,
        ]);
      }
    }
  }
  return [HEADER, ...rows]
    .map((row) => row.map(escapeCsv).join(','))
    .join('\r\n');
}

export const exportCostCsv = (report: CrossOrgCostResponse): void => {
  // UTF-8 BOM so Excel reads it as UTF-8; mirrors the eval-report CSV download.
  const blob = new Blob(['﻿' + buildCostCsv(report)], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `cost-report-${report.start_date}-to-${report.end_date}.csv`;
  // Attach before clicking — some browsers ignore a detached link click.
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
